import { memo } from "react";

// MUI
import { Divider, Grid, Typography } from "@mui/material";

// Components
import CollapsibleCard from "@/Layouts/components/Cards/CollapsibleCard";
import TimesTable from "@/DataPages/components/TimeAndPayRates/Tables/Times/TimesTable";
import PayRatesTable from "@/DataPages/components/TimeAndPayRates/Tables/PayRates/PayRatesTable";
import PtoTable from "@/DataPages/components/TimeAndPayRates/Tables/Pto/PtoTable";

// Types
import { IDepartment, IDepartmentPositionRecord } from "@/DataPages/types/timeAndPayRates/models";
import moment from "moment";
import { or } from "@/Common/helpers/sonar";

// Constants
import { DATE_FORMAT } from "@/Constants/date";

interface IRenderDepartmentTable {
	renderOption: string;
	records?: IDepartmentPositionRecord[];
	position: string;
	department: string;
}
const RenderDepartmentTable = (props: IRenderDepartmentTable) => {
	const { records, renderOption, position } = props;
	const filterKeySuffix = `${props.department}/${position?.replaceAll(" ", "-").toLowerCase()}`;

	switch (renderOption) {
		case "payRates":
			return <PayRatesTable rows={records ?? []} filterKeySuffix={filterKeySuffix} />;
		case "pto":
			return <PtoTable data={records ?? []} filterKeySuffix={filterKeySuffix} />;
		case "timecard":
			return <TimesTable rows={records ?? []} filterKeySuffix={filterKeySuffix} />;
		default:
			return null;
	}
};

interface IRenderDepartmentProps {
	content: IDepartment;
	searchValue: string;
	renderOption: string;
}
const Department = (props: IRenderDepartmentProps) => {
	const { content, searchValue, renderOption } = props;
	if (!content.listPositions?.length) {
		return null;
	}
	const FULL_WIDTH = 12;
	const sxSize = renderOption === "timecard" ? FULL_WIDTH : 4;
	const department = content.department.replaceAll(" ", "");

	function globalFilter(records: IDepartmentPositionRecord[]) {
		const search = searchValue.toLowerCase();
		if (!search) {
			return records;
		}

		return records.filter((row) => filterPayRates(row, search) || filterPto(row, search) || filterTimeCard(row, search));
	}

	function filterPayRates(row: IDepartmentPositionRecord, search: string) {
		if (renderOption !== "payRates") {
			return false;
		}

		const fullName = row?.fullName.toLowerCase() ?? "";
		const hours = !!row?.hours || row.hours === 0 ? row.hours.toFixed(2) : "X";
		const rate = !!row?.rate || row.rate === 0 ? row.rate.toFixed(2) : "";

		return or(fullName.includes(search), hours.includes(search), rate.includes(search));
	}

	function filterPto(row: IDepartmentPositionRecord, search: string) {
		if (renderOption !== "pto") {
			return false;
		}

		const fullName = row?.fullName.toLowerCase() ?? "";
		const balance = !!row?.balance || Number(row.balance) === 0 ? Number(row.balance).toFixed(2) : "";

		return fullName.includes(search) || balance.includes(search);
	}

	function filterTimeCard(row: IDepartmentPositionRecord, search: string) {
		if (renderOption !== "timecard") {
			return false;
		}

		const fullName = row?.fullName.toLowerCase() ?? "";
		const overtime = or(!!row?.overtime, row.overtime === 0) ? row.overtime.toFixed(2) : "";
		const regular = or(!!row?.regular, row.regular === 0) ? row.regular.toFixed(2) : "";
		const totalPaid = or(!!row?.totalPaid, row.totalPaid === 0) ? row.totalPaid.toFixed(2) : "";
		const unpaid = or(!!row?.unpaid, row.unpaid === 0) ? row.unpaid.toFixed(2) : "";
		const date = row?.date ? moment.utc(row.date).format(DATE_FORMAT.usDateSlash).toLowerCase() : "";
		const clockInTime = row?.clockInTime ? moment.utc(row.clockInTime).format(DATE_FORMAT.time12h).toLowerCase() : "";
		const clockOutTime = row?.clockOutTime ? moment.utc(row.clockOutTime).format(DATE_FORMAT.time12h).toLowerCase() : "";

		const allConditions = [
			fullName.includes(search),
			overtime.includes(search),
			regular.includes(search),
			totalPaid.includes(search),
			unpaid.includes(search),
			date.includes(search),
			clockInTime.includes(search),
			clockOutTime.includes(search),
		];

		return allConditions.some((condition) => condition);
	}

	return (
		<Grid key={content.department.replaceAll(" ", "")} item xs={12} mb={"-25px"}>
			<CollapsibleCard
				expanded={true}
				sxActions={{ px: 0, border: "0px solid black" }}
				customTitle={content.department}
				customTitleClasses="!text-[24px] !font-montserrat !font-medium"
			>
				<Grid container columnSpacing={4}>
					{content?.listPositions?.map((element) => {
						if (!element.records?.length) {
							return null;
						}
						return (
							<Grid item xs={sxSize} key={element.position}>
								<Typography className={"pl-4 !text-neutral-gray !text-[18px] !font-montserrat !font-medium"}>
									{element.position}
								</Typography>
								<Grid item mb={4}>
									<RenderDepartmentTable
										position={element?.position ?? ""}
										renderOption={renderOption}
										department={department}
										records={globalFilter(element.records)}
									/>
								</Grid>
							</Grid>
						);
					})}
				</Grid>
			</CollapsibleCard>
			<Divider className="!border-disabled !mt-6 !mb-8 " />
		</Grid>
	);
};

export default memo(Department);
